import {
  subDays,
  subMonths,
  subYears,
  startOfYear,
  differenceInDays,
  format,
  parseISO,
  isValid,
  getMonth,
  getYear,
  differenceInCalendarDays,
  eachMonthOfInterval,
} from "date-fns";

// ---------------------------------------------------------------------------
// 1. Date range presets
// ---------------------------------------------------------------------------

/**
 * Converts a preset string or custom object into { start: Date, end: Date }.
 *
 * Presets: "7days" | "30days" | "90days" | "12months" | "ytd"
 * Custom:  { start: Date|string, end: Date|string } passed through unchanged.
 */
export const getDateRange = (preset) => {
  const today = new Date();

  if (preset && typeof preset === "object") {
    return {
      start:
        typeof preset.start === "string"
          ? parseISO(preset.start)
          : preset.start,
      end: typeof preset.end === "string" ? parseISO(preset.end) : preset.end,
    };
  }

  switch (preset) {
    case "7days":
      return { start: subDays(today, 7), end: today };
    case "30days":
      return { start: subDays(today, 30), end: today };
    case "90days":
      return { start: subDays(today, 90), end: today };
    case "12months":
      return { start: subMonths(today, 12), end: today };
    case "ytd":
      return { start: startOfYear(today), end: today };
    default:
      return { start: subDays(today, 30), end: today };
  }
};

// ---------------------------------------------------------------------------
// 2. Comparison period
// ---------------------------------------------------------------------------

/**
 * Returns the comparison date range for a given primary range.
 *
 * comparisonType:
 *   "previousPeriod" - same duration immediately preceding
 *   "lastMonth"      - same dates shifted back one month
 *   "lastYear"       - same dates shifted back one year
 *   "none"           - returns null
 */
export const getComparisonRange = (dateRange, comparisonType) => {
  if (!dateRange || comparisonType === "none") return null;

  const { start, end } = dateRange;

  switch (comparisonType) {
    case "previousPeriod": {
      const days = differenceInDays(end, start);

      return {
        start: subDays(start, days),
        end: subDays(end, days),
      };
    }
    case "lastMonth":
      return {
        start: subMonths(start, 1),
        end: subMonths(end, 1),
      };
    case "lastYear":
      return {
        start: subYears(start, 1),
        end: subYears(end, 1),
      };
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// 3. Currency formatting
// ---------------------------------------------------------------------------

/**
 * Formats a number as compact currency.
 *   - >= 1 000 000  -> "$1.2M"
 *   - >= 1 000      -> "$12.3K"
 *   - otherwise      -> "$12,345"
 */
export const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "$0";

  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  }

  return `${sign}$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

// ---------------------------------------------------------------------------
// 4. Percentage formatting
// ---------------------------------------------------------------------------

/**
 * Formats a number as a percentage string, e.g. "12.5%".
 */
export const formatPercentage = (value) => {
  if (value == null || isNaN(value)) return "0%";

  return `${parseFloat(value).toFixed(1)}%`;
};

// ---------------------------------------------------------------------------
// 5. Delta calculation
// ---------------------------------------------------------------------------

/**
 * Computes the change between two values.
 * Returns { value: "+12.5%", trend: "up" | "down" | "neutral" }
 */
export const calculateDelta = (current, previous) => {
  if (previous === 0 || previous == null) {
    if (current > 0) return { value: "+100%", trend: "up" };
    if (current < 0) return { value: "-100%", trend: "down" };

    return { value: "0%", trend: "neutral" };
  }

  const change = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.abs(change).toFixed(1);

  if (change > 0) return { value: `+${rounded}%`, trend: "up" };
  if (change < 0) return { value: `-${rounded}%`, trend: "down" };

  return { value: "0%", trend: "neutral" };
};

// ---------------------------------------------------------------------------
// 6. Group by month
// ---------------------------------------------------------------------------

/**
 * Groups array items by month, summing `valueField` per month.
 * Returns [{ month: "Jan", value: 12000 }, ...] for every month in the range.
 *
 * If `items` is empty, returns 12 months (Jan-Dec) with 0 values.
 */
export const groupByMonth = (
  items,
  dateField = "date",
  valueField = "amount"
) => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (!items || items.length === 0) {
    return monthNames.map((month) => ({ month, value: 0 }));
  }

  // Accumulate by "YYYY-MM" key
  const buckets = {};

  items.forEach((item) => {
    const raw = item[dateField];
    const d = raw instanceof Date ? raw : parseISO(raw);

    if (!isValid(d)) return;

    const key = `${getYear(d)}-${String(getMonth(d)).padStart(2, "0")}`;

    if (!buckets[key]) {
      buckets[key] = { month: monthNames[getMonth(d)], value: 0 };
    }
    buckets[key].value += Number(item[valueField]) || 0;
  });

  // Return sorted chronologically
  const sorted = Object.keys(buckets).sort();

  return sorted.map((key) => buckets[key]);
};

// ---------------------------------------------------------------------------
// 7. Count items by month
// ---------------------------------------------------------------------------

/**
 * Counts items per month, returning the last `limit` months.
 * Returns { counts: number[], categories: string[] }.
 *
 * @param {Array} items - Array of objects with a date field.
 * @param {string} dateField - Name(s) to try, e.g. "created_at" or "createdAt".
 * @param {number} limit - Max months to return (default 12).
 */
export const countByMonth = (items, dateField = "created_at", limit = 12) => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const buckets = {};

  (items || []).forEach((item) => {
    const raw = item[dateField] || item.createdAt || item.created_at;
    const d = raw instanceof Date ? raw : new Date(raw);

    if (!d || isNaN(d.getTime())) return;

    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;

    if (!buckets[key]) {
      buckets[key] = { month: monthNames[d.getMonth()], count: 0 };
    }
    buckets[key].count += 1;
  });

  const sortedKeys = Object.keys(buckets).sort();
  const recentKeys = sortedKeys.slice(-limit);

  return {
    counts: recentKeys.map((k) => buckets[k].count),
    categories: recentKeys.map((k) => buckets[k].month),
  };
};

// ---------------------------------------------------------------------------
// 8. Bucket by age
// ---------------------------------------------------------------------------

/**
 * Buckets items by age (days from today).
 *
 * Returns:
 * [
 *   { range: "Current",    count: 42, amount: 45230, percentage: 36 },
 *   { range: "1-30 days",  count: 28, amount: 32450, percentage: 26 },
 *   { range: "31-60 days", count: 15, amount: 18200, percentage: 15 },
 *   { range: "61-90 days", count:  8, amount: 12500, percentage: 10 },
 *   { range: "90+ days",   count:  5, amount: 16212, percentage: 13 },
 * ]
 */
export const bucketByAge = (items, dateField = "date") => {
  const ranges = [
    { range: "Current", min: 0, max: 0 },
    { range: "1-30 days", min: 1, max: 30 },
    { range: "31-60 days", min: 31, max: 60 },
    { range: "61-90 days", min: 61, max: 90 },
    { range: "90+ days", min: 91, max: Infinity },
  ];

  const buckets = ranges.map((r) => ({
    range: r.range,
    count: 0,
    amount: 0,
    percentage: 0,
  }));

  const today = new Date();

  if (!items || items.length === 0) return buckets;

  items.forEach((item) => {
    const raw = item[dateField];
    const d = raw instanceof Date ? raw : parseISO(raw);

    if (!isValid(d)) return;

    const age = differenceInCalendarDays(today, d);

    for (let i = 0; i < ranges.length; i++) {
      if (age >= ranges[i].min && age <= ranges[i].max) {
        buckets[i].count += 1;
        buckets[i].amount += Number(item.amount) || 0;
        break;
      }
    }
  });

  const totalAmount = buckets.reduce((sum, b) => sum + b.amount, 0);

  if (totalAmount > 0) {
    buckets.forEach((b) => {
      b.percentage = Math.round((b.amount / totalAmount) * 100);
    });
  }

  return buckets;
};

// ---------------------------------------------------------------------------
// 8. Month labels
// ---------------------------------------------------------------------------

/**
 * Returns an array of month abbreviation strings between two dates (inclusive).
 * e.g. getMonthLabels(new Date('2025-01-15'), new Date('2025-04-10'))
 *   => ["Jan", "Feb", "Mar", "Apr"]
 */
export const getMonthLabels = (start, end) => {
  if (!start || !end) return [];

  const s = start instanceof Date ? start : parseISO(start);
  const e = end instanceof Date ? end : parseISO(end);

  if (!isValid(s) || !isValid(e)) return [];

  const months = eachMonthOfInterval({ start: s, end: e });

  return months.map((d) => format(d, "MMM"));
};
